// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Rectangle } from '@shared/commons/Geometry';
import { SolidBrush } from '@shared/commons/Graphics';
import { HeatmapSymbolFirstValue, HeatmapSymbolSecondValue, HeatmapSymbolTitleBy } from '@shared/commons/Heatmap/Enums';
import { HeatMapFontManagerParams } from '@shared/commons/Heatmap/Settings/HeatMapFontManagerParams';
import { HeatmapBaseRenderer } from './HeatmapBaseRenderer';
import { HeatmapLogoProvider } from '../HeatmapLogoProvider';
import { ThemeManager } from '../../../misc/ThemeManager';
import { HeatmapSectorRenderer } from './HeatmapSectorRenderer';
export class HeatmapSymbolRenderer extends HeatmapBaseRenderer {
    constructor(symbolMapItem, settings) {
        super();
        this.heatMapFontManagerParams = new HeatMapFontManagerParams();
        this.symbolMapItem = symbolMapItem;
        this.settings = settings;
    }
    draw(gr, mousePoint) {
        if (!this.symbolMapItem.Visible) {
            return;
        }
        const rect = this.symbolMapItem.Rectangle;
        const parentRect = this.symbolMapItem.parentIndustryMap.bodyRectangle;
        let width = rect.Width;
        let height = rect.Height;
        // Adjust width and height based on the parent rectangle
        if (rect.X + rect.Width !== parentRect.X + parentRect.Width) {
            width--;
        }
        if (rect.Y + rect.Height !== parentRect.Y + parentRect.Height) {
            height--;
        }
        const backgroundBrush = new SolidBrush(this.symbolMapItem.getSymbolBackgroundColor());
        const textBrush = new SolidBrush(this.getDefaultSymbolForeground());
        const hoveredSubstrateBrush = new SolidBrush(ThemeManager.colors.heatmapSymbolLevelHoveredSubstrateBackground);
        const inHovered = this.isHovered(mousePoint);
        const drawRect = new Rectangle(rect.X, rect.Y, width, height);
        gr.FillRectWithRect(backgroundBrush, drawRect);
        if (inHovered) {
            gr.FillRectWithRect(hoveredSubstrateBrush, drawRect);
        }
        const title = this.settings.symbolLevelTitleBy === HeatmapSymbolTitleBy.Symbol
            ? this.symbolMapItem.heatmapData.symbol
            : this.symbolMapItem.heatmapData.symbolDescription;
        const firstValue = this.settings.symbolLevelFirstValue === HeatmapSymbolFirstValue.ChangePercentage
            ? this.symbolMapItem.heatmapData.changePercentString
            : '';
        let secondValue = '';
        switch (this.settings.symbolLevelSecondValue) {
            case HeatmapSymbolSecondValue.None:
                secondValue = '';
                break;
            case HeatmapSymbolSecondValue.LastPrice:
                secondValue = this.symbolMapItem.heatmapData.lastPriceString;
                break;
            case HeatmapSymbolSecondValue.MarketCap:
                secondValue = this.symbolMapItem.heatmapData.marketCapString;
                break;
        }
        let logoSize = 24;
        this.heatMapFontManagerParams.logoSize =
            this.settings.logoVisible && HeatmapLogoProvider.isLogoExist(this.symbolMapItem.heatmapData.logoAddress)
                ? logoSize
                : -1;
        this.heatMapFontManagerParams.title = title;
        this.heatMapFontManagerParams.firstValue = firstValue;
        this.heatMapFontManagerParams.secondValue = secondValue;
        this.heatMapFontManagerParams.rectangle = rect;
        this.heatMapFontManagerParams.graphics = gr;
        const result = this.settings.getSymbolFontResult(this.heatMapFontManagerParams);
        if (isNullOrUndefined(result)) {
            if (inHovered) {
                this.drawSelection(gr, drawRect);
            }
            return;
        }
        const logoRect = result.logoRectangle;
        logoSize = result.logoRectangle.Height;
        const titleRect = result.titleRectangle;
        const titleFont = result.titleFont;
        const firstValueRect = result.firstValueRectangle;
        const firstValueFont = result.firstValueFont;
        const secondValueRect = result.secondValueRectangle;
        const secondValueFont = result.secondValueFont;
        let logoImage = null;
        if (logoSize > 0) {
            logoImage = HeatmapLogoProvider.getLogo(this.symbolMapItem.heatmapData.logoAddress);
        }
        if (!logoRect.IsEmpty() && !isNullOrUndefined(logoImage)) {
            gr.drawImage(logoImage, logoRect.X, logoRect.Y, logoRect.Width, logoRect.Height);
        }
        if (!titleRect.IsEmpty()) {
            gr.DrawStringInRect(title, titleFont, textBrush, titleRect, false);
        }
        if (!firstValueRect.IsEmpty()) {
            gr.DrawStringInRect(firstValue, firstValueFont, textBrush, firstValueRect, false);
        }
        if (!secondValueRect.IsEmpty()) {
            gr.DrawStringInRect(secondValue, secondValueFont, textBrush, secondValueRect, false);
        }
        if (inHovered) {
            this.drawSelection(gr, drawRect);
        }
    }
    drawSelection(gr, rectangle) {
        const strokeLineWidth = HeatmapSectorRenderer.HOVERWIDTH;
        const strokeOffset = strokeLineWidth / 2;
        gr.strokeStyle = ThemeManager.colors.HeatmapHeaderBackgroundHovered;
        gr.lineWidth = strokeLineWidth;
        gr.strokeRect(rectangle.X - strokeOffset, rectangle.Y - strokeOffset, rectangle.Width + strokeLineWidth, rectangle.Height + strokeLineWidth);
    }
    isHovered(mousePoint) {
        if (!this.symbolMapItem.Visible) {
            return false;
        }
        return !mousePoint.IsEmpty() && this.symbolMapItem.Rectangle.Contains(mousePoint.X, mousePoint.Y);
    }
    getData() {
        return this.symbolMapItem.heatmapData;
    }
    getDefaultSymbolForeground() {
        var _a;
        return (_a = this.settings.symbolLevelFontColor) !== null && _a !== void 0 ? _a : ThemeManager.colors.heatmapSymbolLevelForeground;
    }
}
